import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '../store/sessionStore.js';
import { useAuthStore }    from '../store/authStore.js';
import { getTerminals, openSession, getActiveSession } from '../api/pos.api.js';
import styles from './OpenSessionScreen.module.css';

const LogoutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

export default function OpenSessionScreen() {
  const navigate       = useNavigate();
  const openSess       = useSessionStore((s) => s.openSession);
  const closeSess      = useSessionStore((s) => s.closeSession);
  const user           = useAuthStore((s) => s.user);
  const logout         = useAuthStore((s) => s.logout);

  const [terminals,    setTerminals]    = useState([]);
  const [terminalId,   setTerminalId]   = useState('');
  const [openingCash,  setOpeningCash]  = useState('');
  const [loading,      setLoading]      = useState(false);
  const [loadingTerms, setLoadingTerms] = useState(true);
  const [error,        setError]        = useState(null);

  useEffect(() => {
    getTerminals()
      .then((data) => {
        setTerminals(data);
        if (data.length === 1) setTerminalId(data[0]._id);
      })
      .catch(() => setError('No se pudieron cargar los terminales'))
      .finally(() => setLoadingTerms(false));
  }, []);

  const handleLogout = () => {
    closeSess();       // limpia cualquier resto de sesión de caja
    logout();          // borra token/usuario → PrivateRoute redirige a /login
    navigate('/login', { replace: true });
  };

  const handleOpen = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // Verificar si ya hay sesión activa en este terminal
      const active = await getActiveSession(terminalId);
      if (active) {
        const terminal = terminals.find((t) => t._id === terminalId);
        openSess({ session: active, terminal });
        navigate('/', { replace: true });
        return;
      }

      const session  = await openSession({ terminal_id: terminalId, opening_cash: Number(openingCash) || 0 });
      const terminal = terminals.find((t) => t._id === terminalId);
      openSess({ session, terminal });
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error ?? 'Error al abrir caja');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <div className={styles.header}>
          <img src="/favicon.svg" alt="Geek Commerce" className={styles.icon} />
          <h1 className={styles.title}>Apertura de caja</h1>
          <p className={styles.sub}>Hola, <strong>{user?.name}</strong></p>
        </div>

        <form className={styles.form} onSubmit={handleOpen}>
          <div className={styles.field}>
            <label className={styles.label}>Terminal</label>
            {loadingTerms ? (
              <div className={styles.loading}><span className="spinner" /></div>
            ) : (
              <select
                className="input"
                value={terminalId}
                onChange={(e) => setTerminalId(e.target.value)}
                required
              >
                <option value="">Seleccionar terminal…</option>
                {terminals.map((t) => (
                  <option key={t._id} value={t._id}>{t.name}</option>
                ))}
              </select>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Efectivo en caja al abrir</label>
            <div className={styles.currencyInput}>
              <span className={styles.currencySymbol}>$</span>
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={openingCash}
                onChange={(e) => setOpeningCash(e.target.value)}
                style={{ paddingLeft: 32 }}
              />
            </div>
            <p className={styles.hint}>Cuenta el efectivo inicial antes de abrir</p>
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <button
            className="btn btn--primary btn--full"
            type="submit"
            disabled={loading || !terminalId}
          >
            {loading ? <span className="spinner" /> : 'Abrir caja'}
          </button>
        </form>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.logoutBtn}
            onClick={handleLogout}
            disabled={loading}
          >
            <LogoutIcon />
            Cerrar sesión de {user?.name?.split(' ')[0] ?? 'cajero'}
          </button>
        </div>
      </div>
    </div>
  );
}
